// Imports
import posthog from 'posthog-js'
import firebase from '@/api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import lensAPI from '@/api/lens'
import GrafanaAPI from '@/api/grafana'
import API from '@/api'
import router from '../../router'
import store from '../index'
import { wipeDB } from '../../db/common'
import 'firebase/app-check'
import _ from 'lodash'
import { SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'
import { isAppRedirect } from '@/utils/auth/isAppRedirect'

const basePlanIds = ['basic_plan', 'workflow_plan', 'agency_plan']
const legacyPlanIds = ['prod_LfIi8NmO2xjOpY', 'prod_K9H5hPoU2WPBuU']

// Auth Module
export default {
  namespaced: true,
  state: {
    user: {},
    subscription: {},
    externalSubscription: {},
    team: null,
    pendingInvitations: [],
    pendingLensGuestInvitations: [],
    stripeCustomer: {},
    invoices: [],
    initialLoad: null,
    teamSubscriptionState: null,
    teamSubscription: null,
    getStarredBoards: [],
    showUpsell: false,
    upsellStep: 0,
    trialState: {},
    appLimits: {
      spyder: 0,
      lens: 0,
      additionalLens: 0,
      hasApiAccess: false
    },
    isEmailVerified: false,
    guestStatus: {
      isGuestOnly: false,
      isGuest: false
    },
    subscriptionUnsubscribe: null,
    loading: {
      invoices: true
    }
  },
  getters: {
    // Get User object
    getUser: state => state.user,

    // Get initial load state
    getInitialLoad: state => state.initialLoad,

    // Get User full name
    getUserName: state => `${state.user.first_name || ''} ${state.user.last_name || ''}`.trim() || state.user.name || state.user.email,

    // Get User first name
    getUserFirstName: state => state.user.first_name || '',

    // Get User email
    getUserEmail: state => state.user.email,

    // Get User avatar
    getUserAvatar: state => state.user.avatar,

    // Get User token
    getUserToken: state => state.user.token,

    getUserFreeTrialDate: state => {
      // Stripe trial end date is in S and firestore trialStarted is in ms. Ensure we use s
      if (state.subscription?.trialEndAt) {
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
        const trialEndAtMs = state.subscription.trialEndAt * 1000
        return trialEndAtMs - sevenDaysMs
      }
      return state.user.trialStarted
    },

    getUserOnboardingStatus: state => state.user?.onboardingStatus ?? 'FINISH',

    // Check if User is logged in
    isUserLoggedIn: state => state.user.email,

    // Get User Subscription
    getUserSubscription: state => state.subscription,

    // Get User External Subscription
    getUserExternalSubscription: state => state.externalSubscription,

    // Get User StripeCustomer
    getStripeCustomer: state => state.stripeCustomer,

    // Get User Invoices
    getUserInvoices: state => state.invoices,

    getInvoicesLoading: state => state.loading.invoices,

    // Check if User is on free tier
    isFreeTier: state => state.subscription?.status === SUBSCRIPTION_STATES.TRIALING,

    // Get Pending Invitations
    getPendingInvitations: state => state.pendingInvitations,

    // Get Pending lens guest invitations
    getPendingLensInvitations: state => state.pendingLensGuestInvitations,

    // Get Team object
    getTeam: state => state.team,

    // Check if User has viewed Survey
    getSurveyViewed: state => state.user.surveyViewed,

    // Current team subscription
    getTeamSubscriptionState: state => state.teamSubscriptionState,

    getTeamSubscription: state => state.teamSubscription,

    getStarredBoards: state => state.getStarredBoards,

    // Check if the user is the team owner
    isTeamOwner: state => state.subscription.isAdmin,

    // Check if we should show the upsell modal in Library
    getShowUpsell: state => state.showUpsell,

    // Get the upsell step
    getUpsellStep: state => state.upsellStep,

    // Check if user is a beta user
    isBetaUser: (state, getters, rootState) => {
      const userId = state.user?.user_id
      const betaUsers = rootState.MiscModule.betaUsers.map(user => user.user_id)
      const currentTeam = state.team
      return betaUsers.includes(currentTeam?.created_by) || betaUsers.includes(userId)
    },

    // Get the trial state
    getTrialState: state => state.trialState,

    getIsSubscriptionActive: state => state.subscription?.status === SUBSCRIPTION_STATES.ACTIVE || state.subscription?.status === SUBSCRIPTION_STATES.TRIALING,

    // Get users app limits (spyder & lens)
    getSpyderLimit: state => state.appLimits?.spyder || 0,
    getLensLimit: state => state.appLimits?.lens || 0,
    getAdditionalLensLimit: state => state.appLimits?.additionalLens || 0,
    getTotalLensLimit: state => (state.appLimits?.lens || 0) + (state.appLimits?.additionalLens || 0),
    getIsLegacy: state => state.subscription.internalProductId === 'legacy_full_workflow_plan' || state.subscription.internalProductId === 'legacy_inspiration_plan',
    getHasApiAccess: state => state.externalSubscription && state.externalSubscription?.status === SUBSCRIPTION_STATES.ACTIVE,
    getApiSubscription: state => state.externalSubscription,
    getBasePlanInterval: state => state.subscription.interval,
    isEmailVerified: state => state.isEmailVerified,
    getGuestStatus: state => state.guestStatus
  },
  mutations: {
    // Set User Mutation
    SET_USER (state, user) {
      state.user = user
    },
    // Set User Mutation
    SET_INITIAL_LOAD (state) {
      state.initialLoad = true
    },
    // Set User Subscription
    SET_USER_SUBSCRIPTION (state, subscription) {
      state.subscription = subscription
    },
    // Set External Subscription (for public api or future products)
    SET_EXTERNAL_SUBSCRIPTION (state, subscription) {
      state.externalSubscription = subscription
    },
    // Set User Invoices
    SET_USER_INVOICES (state, invoices) {
      state.invoices = invoices
    },
    // Set User Subscription
    SET_USER_STRIPE_CUSTOMER (state, customer) {
      state.stripeCustomer = customer
    },
    // Set User Pending Invitations
    SET_USER_INVITATIONS (state, invitations) {
      state.pendingInvitations = invitations
    },
    // Set User Pending Lens Guest Invitations
    SET_PENDING_LENS_GUEST_INVITATIONS (state, invitations) {
      state.pendingLensGuestInvitations = invitations
    },
    // Set User Pending Invitations
    SET_USER_TEAM (state, team) {
      state.team = team
    },
    // Set Onboarding Complete
    SET_ONBOARDING_COMPLETE (state) {
      state.user.onboardingComplete = true
    },
    SET_PRODUCTHUNT_MODAL_VIEWED (state) {
      state.user.viewedProductHuntModal = true
    },
    SET_SURVEY_VIEWED (state) {
      state.user = {
        ...state.user,
        surveyViewed: true
      }
    },
    SET_TEAM_SUBSCRIPTION_STATE (state, subscriptionState) {
      state.teamSubscriptionState = subscriptionState
    },
    SET_TEAM_SUBSCRIPTION (state, subscription) {
      state.teamSubscription = subscription
    },
    SET_STARRED_BOARDS (state, boards) {
      state.getStarredBoards = boards
    },
    SET_USER_IG_USERNAME (state, username) {
      state.user.igUsername = username
    },
    SET_USER_IG_BOARD_IDS (state, boardIds) {
      state.user.igBoardIds = boardIds
    },
    SET_SHOW_UPSELL (state, showUpsell) {
      state.showUpsell = showUpsell
    },
    SET_UPSELL_STEP (state, step) {
      state.upsellStep = step
    },
    SET_REMOVE_DEFAULT_LANGUAGES (state) {
      state.user.defaultLanguages = []
    },
    SET_APP_LIMITS (state, limits) {
      state.appLimits = {
        ...state.appLimits,
        ...limits
      }
    },
    SET_IS_EMAIL_VERIFIED (state, isEmailVerified) {
      state.isEmailVerified = isEmailVerified
    },
    SET_TRIAL_STATE (state, subscription) {
      state.trialState = {
        isActive: true,
        endDate: new Date(subscription.currentPeriodEnd.seconds * 1000),
        endsIn: {
          days: Math.ceil((subscription.currentPeriodEnd.seconds * 1000 - Date.now()) / (1000 * 60 * 60 * 24)),
          hours: Math.ceil((subscription.currentPeriodEnd.seconds * 1000 - Date.now()) / (1000 * 60 * 60)),
          minutes: Math.ceil((subscription.currentPeriodEnd.seconds * 1000 - Date.now()) / (1000 * 60)),
          seconds: Math.ceil((subscription.currentPeriodEnd.seconds * 1000 - Date.now()) / 1000)
        }
      }
    },
    SET_GUEST_STATUS (state, guestStatus) {
      state.guestStatus = guestStatus
    },
    SET_SUBSCRIPTION_UNSUBSCRIBE (state, unsubscribe) {
      state.subscriptionUnsubscribe = unsubscribe
    },
    SET_LOADING (state, loading) {
      state.loading = {
        ...state.loading,
        ...loading
      }
    }
  },
  actions: {
    async debugResetStore ({ commit, state }) {
      commit('SET_USER', {})
      commit('SET_USER_SUBSCRIPTION', {})
      commit('SET_EXTERNAL_SUBSCRIPTION', {})
      commit('SET_USER_STRIPE_CUSTOMER', {})
      commit('SET_USER_INVOICES', [])
      window.location.reload()
    },
    async fetchUserSubscription ({ commit }, ignoreCache = false) {
      try {
        const { customer, plan, apiPlan, isAdmin } = await ForeplayAPI.Users.getSubscription(ignoreCache) || {}
        const basePlan = {
          ...plan,
          isAdmin
        }
        commit('SET_EXTERNAL_SUBSCRIPTION', apiPlan)
        commit('SET_USER_SUBSCRIPTION', basePlan)
        commit('SET_USER_STRIPE_CUSTOMER', customer)
        return { basePlan, apiPlan, customer }
      } catch (e) {
        console.error('Error setting subscription', e)
      }
    },
    async fetchAppLimits ({ commit, state }, limitSnapshot = null) {
      const hasSnapshot = limitSnapshot ? Object.values(limitSnapshot).every(value => value !== undefined) : false
      const limitOwner = state?.team?.created_by || state.user?.user_id
      const userSubscription = state.subscription

      const paidLens = userSubscription?.addOns?.lens?.quantity || 0
      const paidSpyder = userSubscription?.addOns?.spyder?.quantity || 0
      const additionalLensLimit = userSubscription?.addOns?.additionalLens?.quantity || 0

      const { spyderLimit: spyderOverride = 0, lensLimit: lensOverride = 0, additionalLensLimit: additionalLensOverride = 0, lensTrialStarted } = hasSnapshot ? limitSnapshot : await FirebaseAPI.Users.getById(limitOwner)

      const creditLimits = {
        spyder: Math.max(paidSpyder, spyderOverride),
        lens: Math.max(paidLens, lensOverride),
        additionalLens: Math.max(additionalLensLimit, additionalLensOverride)
      }

      // Trial started if no user subscription and trialStarted is within 7 days
      const isTrialActive = userSubscription?.status === SUBSCRIPTION_STATES.TRIALING
      const isLensTrialActive = paidLens === 0 && lensTrialStarted && Date.now() < lensTrialStarted + 6.048e8

      const isBasic = userSubscription?.internalProductId === 'basic_plan'
      const isLegacy = userSubscription?.internalProductId === 'legacy_full_workflow_plan' || userSubscription?.internalProductId === 'legacy_inspiration_plan'

      /** Spyder limits
       * Trial users = 2
       * Legacy users = max of current and default promo (2)
       * New users = included spyderLimit
       */
      const includedSpyders = ((isTrialActive && !isBasic) || isLegacy) ? Math.max(creditLimits.spyder, 2) : creditLimits.spyder || 0
      /** Lens limits
       * Basic & Lens Trial = max of override limit & 1
       * Legacy users = 0
       * New users = included lensLimit
       */
      const includedLens = isLensTrialActive ? Math.max(creditLimits.lens, 1) : creditLimits.lens || 0

      const includedAdditionalLens = creditLimits.additionalLens || 0
      console.log('Fetched app limits:', { includedSpyders, includedLens, isTrialActive, isLensTrialActive, usedAuthUserCache: hasSnapshot })
      commit('SET_APP_LIMITS', {
        spyder: includedSpyders,
        lens: includedLens,
        additionalLens: includedAdditionalLens
      })
    },
    // Login/Signup User With Google
    async loginUserWithGoogle ({ commit, state }) {
      const redirect = router.currentRoute.query.redirect
      const provider = new firebase.auth.GoogleAuthProvider()

      const result = await firebase.auth().signInWithPopup(provider)

      const isNewUser = result.additionalUserInfo.isNewUser
      if (isNewUser) {
        if (window.location.pathname.includes('discovery-experts')) {
          window.Intercom('update', {
            from_experts: true
          })
        }
        const email = result.additionalUserInfo.profile?.email
        // Google authed users automatically have their email verified, so they skip the
        // unverified Lead Created state and go straight to Signed Up
        window.dataLayer.push({
          event: 'email_verified',
          user_data: {
            email: email,
            name: result?.user?.displayName || 'Unknown'
          }
        })

        // Identify before capture() below, so the event lands on this person
        // instead of racing ahead as an orphaned anonymous event.
        posthog.identify(result.user.uid)
        posthog.capture('Signed Up', {
          email: email,
          name: result?.user?.displayName || 'Unknown'
        })

        commit('SET_USER', {
          ...state.user,
          onboardingStatus: 'TEAM'
        })

        // Update Hubspot property
        await ForeplayAPI.Users.validateEmail()
      }

      setTimeout(() => {
        window.rewardful('convert', { email: result.user.email })
      }, 5000)

      await result.user.getIdToken()

      commit('SET_USER', {
        ...state.user,
        email: result.user.email,
        name: result.user.displayName,
        avatar: result.user.photoURL
      })

      // Track the user login event
      await API.Analytics.login({
        email: result.user.email,
        type: 'google',
        utmCampaign: store.getters['MiscModule/getUtmSource'] || '',
        utmMedium: store.getters['MiscModule/getUtmMedium'] || '',
        utmSource: store.getters['MiscModule/getUtmSource'] || ''

      })

      window.analytics.alias(result.user.email)

      authChromeExtension(result.user.user_id)

      // Wipe the db
      await wipeDB()

      // Wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000))

      if (isNewUser) {
        router.push({
          name: 'DiscoveryView',
          query: { email: result?.user?.email }
        }).catch(e => e)
      } else if (isAppRedirect(redirect)) {
        router.push(redirect).catch(e => e)
      } else {
        router.push({ name: 'DiscoveryView' }).catch(e => e)
      }
    },
    async checkTeamExpired ({ commit, state }) {
      if (!state.team) return

      const createdById = state.team.created_by

      const db = firebase.firestore()
      // Check for active subscriptions
      const teamSubscriptionSnapshot = await db.collection('customers').doc(createdById).collection('subscriptions').where('status', 'in', ['trialing', 'active']).get()

      if (teamSubscriptionSnapshot.empty) {
        console.log('No active subscriptions')
        // Get and sort cancelled subscriptions
        const inactiveSubscriptionsSnapshot = await db.collection('customers').doc(createdById).collection('subscriptions').where('status', 'in', ['canceled']).get()
        const teamInactiveSubscriptions = inactiveSubscriptionsSnapshot.docs.map(doc => doc.data()).sort((a, b) => b.canceled_at.seconds - a.canceled_at.seconds)

        // Get past dude subscriptions (they will need to be reactivated)
        const pastDueSubscriptionsSnapshot = await db.collection('customers').doc(createdById).collection('subscriptions').where('status', 'in', ['past_due']).get()

        // If there aren't any past due subscriptions, use the most recent cancelled subscription
        if (pastDueSubscriptionsSnapshot.empty) {
          // Set as active for trial users who are on a team
          if (teamInactiveSubscriptions.length === 0) {
            // We have to check if the team owner is on a free trial or not if the trial started in the last 7 days
            const teamOwner = await FirebaseAPI.Users.getById(createdById)
            const trialStarted = teamOwner.trialStarted

            if (trialStarted && Date.now() - trialStarted < 604800000) {
              commit('SET_TEAM_SUBSCRIPTION_STATE', 'active')
            } else {
              commit('SET_TEAM_SUBSCRIPTION_STATE', 'inactive')
            }

            return
          }

          // Use the most recent subscription
          const mostRecentSubscription = teamInactiveSubscriptions[0]
          commit('SET_TEAM_SUBSCRIPTION_STATE', mostRecentSubscription.status)
          return
        } else {
          const mostRecentSubscription = pastDueSubscriptionsSnapshot.docs[0]
          commit('SET_TEAM_SUBSCRIPTION_STATE', mostRecentSubscription.data().status)
          return
        }
      }

      commit('SET_TEAM_SUBSCRIPTION_STATE', 'active')
      const teamSub = teamSubscriptionSnapshot.docs[0].data()
      if (teamSub.status === 'trialing') {
        commit('SET_TRIAL_STATE', teamSub)
      }
    },
    async fetchFreeTrialDate ({ getters, commit }) {
      const userId = firebase.auth().currentUser
      const stateUser = getters.getUser
      const userData = await FirebaseAPI.Users.getById(userId.uid)
      commit('SET_USER', {
        ...userData,
        ...stateUser,
        user_id: getters.getUser?.user_id,
        trialStarted: userData.trialStarted
      })
    },
    // Sign Up with Email and Password
    async signUpWithEmailAndPassword ({ commit, state }, creds) {
      try {
        const result = await firebase
          .auth()
          .createUserWithEmailAndPassword(creds.email, creds.password)

        // Identify before any capture() calls below, so those events land on
        // this person instead of racing ahead as an orphaned anonymous event.
        posthog.identify(result.user.uid)

        await result.user.getIdToken()

        await result.user.updateProfile({
          displayName: creds.name
        })

        const userPayload = {
          first_name: creds.firstName,
          email: result.user.email,
          name: creds.name,
          trialStarted: Date.now(),
          onboardingStatus: 'TEAM'
        }

        if (creds.lastName) {
          userPayload.last_name = creds.lastName
        }

        await firebase.firestore().collection('auth-users').doc(result.user.uid).set(userPayload, { merge: true })

        setTimeout(() => {
          window.rewardful('convert', { email: creds.email })
        }, 5000)

        commit('SET_USER', {
          ...state.user,
          email: result.user.email,
          name: result.user.displayName,
          avatar: result.user.photoURL,
          onboardingStatus: 'TEAM'
        })

        window.analytics.alias(result.user.email)

        if (window.location.pathname.includes('discovery-experts')) {
          window.Intercom('update', {
            from_experts: true
          })
        }

        authChromeExtension(result.user.user_id)

        // Wipe the db
        await wipeDB()

        // Wait for 1 second
        await new Promise(resolve => setTimeout(resolve, 1000))

        posthog.capture('Lead Created', {
          email: result.user.email,
          name: creds.name
        })

        router.push({ name: 'VerifyEmailView' })
      } catch (error) {
        console.error('Error during sign up:', error)
        throw error
      }
    },
    // Log In With Password
    async loginUserWithEmailAndPassword ({ commit, state }, creds) {
      const redirect = router.currentRoute.query.redirect
      const result = await firebase.auth().signInWithEmailAndPassword(creds.email, creds.password)
      await result.user.getIdToken()

      commit('SET_USER', {
        ...state.user,
        email: result.user.email,
        name: result.user.displayName,
        avatar: result.user.photoURL
      })

      window.analytics.alias(result.user.email)

      authChromeExtension(result.user.user_id)

      // Wait for 1 second
      await new Promise(resolve => setTimeout(resolve, 1000))

      router.push(isAppRedirect(redirect) ? redirect : { name: 'DiscoveryView' }).catch(e => e)
    },
    // Login With Token
    async loginUserWithToken ({ commit, state }, creds) {
      const result = await firebase.auth().signInWithCustomToken(creds.token)
      await result.user.getIdToken()

      commit('SET_USER', {
        ...state.user,
        email: result.user.email,
        name: result.user.displayName,
        avatar: result.user.photoURL
      })

      router.push({ name: 'DiscoveryView' })
    },
    // Logout User
    async logoutUser ({ dispatch }, authFlowOobCode = null) {
      dispatch('unsubscribeFromSubscriptionChanges')
      await firebase
        .auth()
        .signOut()

      // Wipe the db
      await wipeDB()

      // find the current page location and remove '/' from the pathname
      const currentLocation = window.location.pathname.replace('/', '')

      // track the user logout event
      await window.analytics.track('User Logged Out', {
        pageLocation: currentLocation
      })

      if (authFlowOobCode) {
        router.push({ name: 'LoginView', query: { authOob: authFlowOobCode } })
      } else {
        router.push({ name: 'LoginView' })
      }
    },
    async getStripeCustomer ({ commit }, user) {
      const db = firebase.firestore()
      const stripeCustomer = await db.collection('customers').doc(user.uid).get()
      commit('SET_USER_STRIPE_CUSTOMER', stripeCustomer.data())

      return stripeCustomer
    },
    async getUserInvoices ({ commit, state }, user) {
      commit('SET_LOADING', { invoices: true })
      try {
        const { data: { apiInvoices, planInvoices } } = await ForeplayAPI.Subscription.getSubscriptionInvoices()

        const allInvoices = [...(apiInvoices || []), ...(planInvoices || [])]
        commit('SET_USER_INVOICES', allInvoices)
      } catch (error) {
        console.error('Error fetching user invoices', error)
      } finally {
        commit('SET_LOADING', { invoices: false })
      }
    },
    async updateUserSubscription ({ commit, state }) {
      try {
        const { customer, plan, apiPlan, isAdmin } = await ForeplayAPI.Users.getSubscription() || {}
        const basePlan = {
          ...plan,
          isAdmin
        }
        commit('SET_EXTERNAL_SUBSCRIPTION', apiPlan)
        commit('SET_USER_SUBSCRIPTION', basePlan)
        commit('SET_USER_STRIPE_CUSTOMER', customer)
      } catch (error) {
        console.error('Error updating user subscription', error)
      }
    },
    // Set User Subscription
    setUserSubscription ({ commit, state }, user) {
      return new Promise((resolve, reject) => {
        const db = firebase.firestore()
        db.collection('customers')
          .doc(user.uid)
          .collection('subscriptions')
          .where('status', 'in', ['trialing', 'active', 'past_due'])
          .onSnapshot(async doc => {
            if (doc.docs.length) {
              let basePlan = null
              let apiPlan = null

              // As of 8/21/2025, we have 2 subscriptions, one for the base plan and one for the API plan.
              // We need to find both of them and set them in the store.
              for (const sub of doc.docs) {
                const subData = sub.data()
                if (!subData?.items) continue

                // Base plan check TODO @Sam: VERIFY THIS WORKS FOR LEGACY USERS
                if (!basePlan && subData.items.some(item =>
                  item?.price?.product?.metadata?.is_legacy === 'true' || legacyPlanIds.includes(item?.plan?.product) || item.plan?.nickname === 'fullworkflow_annual' || basePlanIds.includes(item.price?.product?.metadata?.internal_product_id)
                )) {
                  basePlan = {
                    ...subData,
                    id: sub?.id
                  }
                }

                // API plan check
                if (!apiPlan && subData.items.some(item =>
                  item.plan?.metadata?.internal_product_id === 'public_api'
                )) {
                  apiPlan = {
                    ...subData,
                    id: sub?.id
                  }
                }
                if (basePlan && apiPlan) break
              }

              if (apiPlan) {
                commit('SET_EXTERNAL_SUBSCRIPTION', apiPlan)
              }
              if (!basePlan) {
                console.log('no base plan')
                resolve({})
                return
              }

              const [priceData, productData] = await Promise.all([
                basePlan.price.get().then(e => e.data()),
                basePlan.product.get().then(e => e.data())
              ])

              const subscription = {
                ...basePlan,
                price: priceData,
                product: productData
              }
              commit('SET_USER_SUBSCRIPTION', subscription)
              if (subscription.status === 'trialing') {
                commit('SET_TRIAL_STATE', subscription)
              }

              resolve(subscription)
            } else {
              resolve({})
            }
          })
      })
    },
    // Set Team subscription
    async setTeamSubscription ({ commit, state }) {
      const db = firebase.firestore()

      const team = state.team
      if (team) {
        const teamSubscriptionSnapshot = await db.collection('customers').doc(team?.created_by).collection('subscriptions').where('status', 'in', ['trialing', 'active']).get()
        if (teamSubscriptionSnapshot.docs.length > 0) {
          let basePlan = null
          let apiPlan = null

          for (const sub of teamSubscriptionSnapshot.docs) {
            const subData = sub.data()
            if (!subData?.items) continue

            if (!basePlan && subData.items.some(item =>
              item?.price?.product?.metadata?.is_legacy === 'true' || legacyPlanIds.includes(item?.plan?.product) || basePlanIds.includes(item.price?.product?.metadata?.internal_product_id)
            )) {
              basePlan = {
                ...subData,
                id: sub?.id
              }
            }

            if (!apiPlan && subData.items.some(item =>
              item.plan?.metadata?.internal_product_id === 'public_api'
            )) {
              apiPlan = {
                ...subData,
                id: sub?.id
              }
            }
            if (basePlan && apiPlan) break
          }

          if (apiPlan) {
            commit('SET_EXTERNAL_SUBSCRIPTION', apiPlan)
          }

          if (!basePlan) {
            return
          }

          const [priceData, productData] = await Promise.all([
            basePlan.price.get().then(e => e.data()),
            basePlan.product.get().then(e => e.data())
          ])

          const subscription = {
            ...basePlan,
            price: priceData,
            product: productData
          }
          commit('SET_TEAM_SUBSCRIPTION', subscription)
        }
      }
    },
    async setGuestAccess ({ commit, state }) {
      const { data: guestStatus } = await lensAPI.Lens.getGuestStatus()
      const sub = state.subscription
      const noUserSub = !sub?.status || sub?.status === SUBSCRIPTION_STATES.NOT_ACTIVE
      const isGuest = guestStatus === 'guest'
      const inActiveUser = sub.status !== 'active'

      const statusMap = {
        isGuestOnly: isGuest && noUserSub,
        isGuest: isGuest && !inActiveUser
      }

      commit('SET_GUEST_STATUS', statusMap)

      // Handle rerouting for guestOnly users
      const currentProduct = store.getters['RouterModule/getCurrentRouteProduct']
      const currentRouteRequiresAuth = router.currentRoute?.meta?.requiresAuth
      if (statusMap.isGuestOnly && currentRouteRequiresAuth && currentProduct && currentProduct !== 'lens') {
        router.push({ name: 'LensView' })
      }
    },
    async fetchUserInvitations ({ commit }) {
      try {
        const {data: userInvitations} = await ForeplayAPI.Users.getInvitations()
        commit('SET_USER_INVITATIONS', userInvitations)
      } catch (e) {
        console.error('Error fetching user invitations from Firebase', e)
        commit('SET_USER_INVITATIONS', [])
      }

      try {
        const { data: lensGuestInvitations } = await lensAPI.Lens.Invite.getAll()
        commit('SET_PENDING_LENS_GUEST_INVITATIONS', lensGuestInvitations)
      } catch (e) {
        console.error('Error fetching lens guest invitations', e)
        commit('SET_PENDING_LENS_GUEST_INVITATIONS', [])
      }
    },
    async fetchUserTeam ({ commit }) {
      // const userTeam = await FirebaseAPI.Teams.getUserTeam()
      const userTeam = await ForeplayAPI.Users.getUserTeam()

      // Check if the user/team has any outgoing invitations
      if (userTeam) {
        try {
          const userInvitations = await FirebaseAPI.Invitations.getAllCreatedByUser()
          userTeam.outgoingInvitations = userInvitations.length
        } catch (e) {
          console.error('Error fetching user invitations from Firebase', e)
          userTeam.outgoingInvitations = 0
        }
      }

      commit('SET_USER_TEAM', userTeam)

      return userTeam
    },
    // Firebase snapshot can be more reliable than the socket notifications, so lets keep both
    async subscribeToSubscriptionChanges ({ commit, state, dispatch }, user) {
      try {
      // Clean up existing listener if any
        if (state.subscriptionUnsubscribe) {
          state.subscriptionUnsubscribe()
        }

        if (!user?.uid) return

        const db = firebase.firestore()
        let isInitialSnapshot = true

        const unsubscribe = db.collection('customers')
          .doc(user.uid)
          .collection('subscriptions')
          .where('status', 'in', ['trialing', 'active', 'past_due'])
          .onSnapshot(async (snapshot) => {
          // Skip the initial snapshot to avoid redundant API calls on mount
            if (isInitialSnapshot) {
              isInitialSnapshot = false
              return
            }

            console.log('Subscription changed via snapshot, refreshing...')

            try {
              const { basePlan } = await dispatch('fetchUserSubscription', true) || {}

              if (basePlan) {
                const spyderLimit = basePlan?.addOns?.spyder?.quantity || 0
                const lensLimit = basePlan?.addOns?.lens?.quantity || 0
                const additionalLensLimit = basePlan?.addOns?.additionalLens?.quantity || 0
                commit('SET_APP_LIMITS', { spyder: spyderLimit, lens: lensLimit, additionalLens: additionalLensLimit })
              }
            } catch (error) {
              console.error('Error refreshing subscription from snapshot change', error)
            }
          })

        commit('SET_SUBSCRIPTION_UNSUBSCRIBE', unsubscribe)
        return unsubscribe
      } catch (error) {
        console.error('Error: snapshot changes', error)
      }
    },
    unsubscribeFromSubscriptionChanges ({ state, commit }) {
      if (state.subscriptionUnsubscribe) {
        state.subscriptionUnsubscribe()
        commit('SET_SUBSCRIPTION_UNSUBSCRIBE', null)
      }
    }
  }
}

async function authChromeExtension (userId) {
  try {
    const createTokenFunction = firebase.functions().httpsCallable('createToken')
    const token = await createTokenFunction(userId)

    // eslint-disable-next-line no-undef
    chrome.runtime.sendMessage('eaancnanphggbfliooildilcnjocggjm', {
      event: 'auth',
      data: { token }
    }, function (response) {
      if (response.status === 'worked') {
        window.Intercom('update', {
          installed_extension: true
        })
      }
    })

    // So this doesnt break for people witht he old ext
    try {
      // Also send this token to the plugin to authenticate the user
      const root = document.querySelector('#FP-PLUGIN')
      const iframe = root?.shadowRoot?.querySelector('.FP-plugin-iframe')
      if (iframe) {
        console.log('posting message')
        iframe.contentWindow.postMessage({
          source: 'fp:app',
          type: 'auth',
          data: { token }
        }, '*')
      }
    } catch (error) {
      console.log(error)
    }
  } catch (error) {
    console.log(error)
  }
}
